"use client";

import { FolderKanban, Paperclip, Plus, Search, XIcon } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useId, useRef, useState } from "react";

import EmojiPicker from "@/app/profile/inbox/(chats)/[chatId]/components/EmojiPicker";
import ComposerLengthCounter from "@/app/profile/inbox/(chats)/components/ComposerLengthCounter";
import {
	MAX_COMPOSER_MESSAGE_LENGTH,
	supportTicketFormFieldTextClassName,
} from "@/app/profile/inbox/(chats)/components/SupportTickets/supportTicketMessageUtils";
import { CHAT_CONSTANTS } from "@/app/profile/inbox/(chats)/constants";
import LoadingSpinner from "@/components/loadingComponents/loadingSpinner";
import { Textarea } from "@/components/ui/textarea";
import { useSearchServicesQuery } from "@/features/services/serviceApiInjection";
import { cn } from "@/lib/utils";
import { formatServiceDisplayTitle } from "@/lib/utils/service-title";
import { getProxiedImageUrl } from "@/lib/utils/image-proxy";
import type { CatalogSuggestion, Service } from "@/types";

const MAX_CATALOG_SUGGESTIONS = 5;
const MAX_ATTACHMENT_COUNT = 10;

function cleanCatalogText(value?: string | null, max = 220) {
	return (value ?? "")
		.replace(/<[^>]*>/g, " ")
		.replace(/\s+/g, " ")
		.trim()
		.slice(0, max);
}

function serviceToCatalogSuggestion(service: Service): CatalogSuggestion {
	const firstImage = service.media?.find((item) => {
		if (!item || item.type?.toUpperCase() === "VIDEO") return false;
		return Boolean(item.displayUrl?.trim() || item.url?.trim());
	});
	const rawImageUrl =
		firstImage?.displayUrl?.trim() ||
		firstImage?.url?.trim() ||
		service.thumbnailImage?.trim() ||
		"";
	const imageUrl = getProxiedImageUrl(rawImageUrl) ?? rawImageUrl;
	const tags =
		service.subcategoryIds
			?.map((category) => category?.title)
			.filter((tag): tag is string => Boolean(tag?.trim()))
			.slice(0, 5) ?? [];

	return {
		type: "service",
		title: formatServiceDisplayTitle(service.title),
		label: service.categoryId?.title || service.eyebrowText || "Service",
		description: cleanCatalogText(service.excerpt || service.description),
		url: `/services/${service.slug || service._id}`,
		imageUrl,
		meta: tags.slice(0, 2).join(" · "),
		tags,
		service,
	};
}

type SupportTicketMessageComposerProps = {
	value: string;
	onChange: (value: string) => void;
	files: File[];
	onFilesChange: (files: File[]) => void;
	catalogSuggestions: CatalogSuggestion[];
	onCatalogSuggestionsChange: (suggestions: CatalogSuggestion[]) => void;
	placeholder?: string;
	rows?: number;
	className?: string;
};

export default function SupportTicketMessageComposer({
	value,
	onChange,
	files,
	onFilesChange,
	catalogSuggestions,
	onCatalogSuggestionsChange,
	placeholder = "Tell us what you need help with",
	rows = 5,
	className,
}: SupportTicketMessageComposerProps) {
	const fileInputId = useId();
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const objectUrlsMapRef = useRef<Map<number, string>>(new Map());
	const [isComposerMenuOpen, setIsComposerMenuOpen] = useState(false);
	const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);

	const handleEmojiSelect = useCallback(
		(emoji: string) => {
			onChange(`${value}${emoji}`);
		},
		[onChange, value],
	);

	const openFilePicker = useCallback(() => {
		setIsComposerMenuOpen(false);
		fileInputRef.current?.click();
	}, []);

	const openCatalogModal = useCallback(() => {
		setIsComposerMenuOpen(false);
		setIsCatalogModalOpen(true);
	}, []);

	const onFileRemove = useCallback(
		(index: number) => {
			const next = [...files];
			next.splice(index, 1);
			onFilesChange(next);
		},
		[files, onFilesChange],
	);

	useEffect(() => {
		const currentUrls = new Map<number, string>();
		files.forEach((file, index) => {
			if (
				file instanceof File &&
				file.type.startsWith("image/") &&
				file.size < CHAT_CONSTANTS.MAX_FILE_SIZE_BYTES
			) {
				currentUrls.set(index, URL.createObjectURL(file));
			}
		});
		objectUrlsMapRef.current.forEach((url, index) => {
			if (!currentUrls.has(index)) URL.revokeObjectURL(url);
		});
		objectUrlsMapRef.current = currentUrls;
		return () => {
			objectUrlsMapRef.current.forEach((url) => URL.revokeObjectURL(url));
			objectUrlsMapRef.current.clear();
		};
	}, [files]);

	return (
		<div className={cn("space-y-2", className)}>
			{catalogSuggestions.length > 0 ? (
				<div className="flex flex-wrap gap-2 rounded-xl bg-accent-foreground/5 p-2 dark:bg-core-midnight/50">
					{catalogSuggestions.map((suggestion) => (
						<div
							key={suggestion.url}
							className="group relative flex items-center gap-2 rounded-lg bg-background p-2 pr-8 text-sm shadow-sm"
						>
							<FolderKanban className="size-4 shrink-0 stroke-secondary" />
							<span className="max-w-48 truncate">
								{formatServiceDisplayTitle(suggestion.title)}
							</span>
							<button
								type="button"
								onClick={() =>
									onCatalogSuggestionsChange(
										catalogSuggestions.filter((item) => item.url !== suggestion.url),
									)
								}
								className="absolute right-1 top-1 rounded-full p-1 opacity-70 transition-opacity hover:bg-destructive/10 hover:opacity-100"
								aria-label={`Remove ${formatServiceDisplayTitle(suggestion.title)}`}
							>
								<XIcon className="size-3" />
							</button>
						</div>
					))}
				</div>
			) : null}
			{files.length > 0 ? (
				<div className="flex flex-wrap gap-2 rounded-xl bg-accent-foreground/5 p-2 dark:bg-core-midnight/50">
					{files.map((file, index) => {
						const imageUrl = objectUrlsMapRef.current.get(index);
						return (
							<div
								key={`${file.name}-${index}`}
								className="group relative flex items-center gap-2 rounded-lg bg-background p-2 pr-8 text-sm shadow-sm"
							>
								{imageUrl ? (
									<Image
										src={imageUrl}
										alt={file.name}
										width={48}
										height={48}
										className="size-12 rounded object-cover"
										unoptimized
									/>
								) : (
									<span className="flex size-12 items-center justify-center text-2xl">
										📎
									</span>
								)}
								<div className="min-w-0 flex-1">
									<span className="block truncate text-xs font-medium">{file.name}</span>
								</div>
								<button
									type="button"
									onClick={() => onFileRemove(index)}
									className="absolute right-1 top-1 rounded-full p-1 opacity-70 transition-opacity hover:bg-destructive/10 hover:opacity-100"
									aria-label={`Remove ${file.name}`}
								>
									<XIcon className="size-3" />
								</button>
							</div>
						);
					})}
				</div>
			) : null}
			<div className="overflow-hidden rounded-md border border-secondary/35 bg-white/85 shadow-sm backdrop-blur-2xl dark:bg-background/25">
				<div className="relative">
					<Textarea
						value={value}
						onChange={(event) => onChange(event.target.value)}
						placeholder={placeholder}
						rows={rows}
						maxLength={MAX_COMPOSER_MESSAGE_LENGTH}
						className={cn(
							"min-h-24 resize-none rounded-none border-0 bg-transparent px-3 py-3 pb-6 shadow-none placeholder:text-muted-foreground focus-visible:ring-0",
							supportTicketFormFieldTextClassName,
						)}
					/>
					<ComposerLengthCounter
						length={value.length}
						className="absolute bottom-2 right-3"
					/>
				</div>
				<div className="flex items-center gap-1 border-t border-secondary/15 px-2 py-1.5">
					<div className="relative">
						<button
							type="button"
							className="inline-flex size-8 items-center justify-center rounded-full transition-colors hover:bg-accent-foreground/10"
							onClick={() => setIsComposerMenuOpen((current) => !current)}
							aria-label="Open message actions"
							aria-expanded={isComposerMenuOpen}
						>
							<Plus
								className={cn(
									"size-4 stroke-accent-foreground transition-transform hover:stroke-accent-foreground/70",
									isComposerMenuOpen && "rotate-45",
								)}
							/>
						</button>
						{isComposerMenuOpen ? (
							<div className="absolute bottom-full left-0 z-20 mb-2 flex min-w-44 flex-col gap-1 rounded-2xl border border-secondary/20 bg-background/95 p-2 text-sm shadow-xl backdrop-blur dark:bg-card/95">
								<button
									type="button"
									className="flex items-center gap-2 rounded-xl px-3 py-2 text-left text-foreground transition hover:bg-secondary/10"
									onClick={openFilePicker}
								>
									<Paperclip className="size-4 stroke-secondary" />
									<span>Attachment</span>
								</button>
								<button
									type="button"
									className="flex items-center gap-2 rounded-xl px-3 py-2 text-left text-foreground transition hover:bg-secondary/10"
									onClick={openCatalogModal}
								>
									<FolderKanban className="size-4 stroke-secondary" />
									<span>Service catalog</span>
								</button>
							</div>
						) : null}
						<input
							id={fileInputId}
							ref={fileInputRef}
							type="file"
							hidden
							multiple
							onChange={(event) => {
								const picked = Array.from(event.target.files ?? []).slice(
									0,
									MAX_ATTACHMENT_COUNT,
								);
								onFilesChange(
									[...files, ...picked].slice(0, MAX_ATTACHMENT_COUNT),
								);
								event.target.value = "";
							}}
							aria-label="Attach files to support ticket message"
						/>
					</div>
					<EmojiPicker
						onEmojiSelect={handleEmojiSelect}
						className="size-8"
						iconClassName="size-4"
					/>
				</div>
			</div>
			<ServiceCatalogPickerModal
				open={isCatalogModalOpen}
				selected={catalogSuggestions}
				onClose={() => setIsCatalogModalOpen(false)}
				onChange={onCatalogSuggestionsChange}
			/>
		</div>
	);
}

function ServiceCatalogPickerModal({
	open,
	selected,
	onClose,
	onChange,
}: {
	open: boolean;
	selected: CatalogSuggestion[];
	onClose: () => void;
	onChange: (suggestions: CatalogSuggestion[]) => void;
}) {
	const [search, setSearch] = useState("");
	const { data, isFetching } = useSearchServicesQuery(
		{
			search,
			page: 1,
			limit: 12,
			catalogParentsOnly: true,
		},
		{ skip: !open },
	);

	if (!open) return null;

	const selectedUrls = new Set(selected.map((item) => item.url));
	const services = data?.docs ?? [];

	const toggleService = (service: Service) => {
		const suggestion = serviceToCatalogSuggestion(service);
		if (selectedUrls.has(suggestion.url)) {
			onChange(selected.filter((item) => item.url !== suggestion.url));
			return;
		}
		if (selected.length >= MAX_CATALOG_SUGGESTIONS) return;
		onChange([...selected, suggestion]);
	};

	return (
		<div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 p-4 sm:items-center">
			<div className="w-full max-w-2xl rounded-2xl border border-secondary/20 bg-background shadow-2xl dark:bg-card">
				<div className="flex items-start justify-between gap-3 border-b border-secondary/20 px-4 py-3">
					<div>
						<p className="text-base font-semibold text-foreground">Service catalog</p>
						<p className="text-xs text-muted-foreground">
							Search and add up to {MAX_CATALOG_SUGGESTIONS} services to this message.
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="rounded-full p-1 text-muted-foreground hover:bg-muted"
						aria-label="Close service catalog"
					>
						<XIcon className="size-5" />
					</button>
				</div>
				<div className="space-y-3 p-4">
					<label className="relative block">
						<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 stroke-secondary/60" />
						<input
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === "Enter") event.preventDefault();
							}}
							placeholder="Search services..."
							className="w-full rounded-xl border border-secondary/20 bg-background py-2 pl-9 pr-3 text-sm outline-none focus:border-secondary/60"
						/>
					</label>
					<div className="text-xs text-muted-foreground">
						{selected.length}/{MAX_CATALOG_SUGGESTIONS} selected
					</div>
					<div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1">
						{isFetching ? (
							<div className="flex justify-center py-8">
								<LoadingSpinner />
							</div>
						) : services.length === 0 ? (
							<p className="py-8 text-center text-sm text-muted-foreground">
								No services found.
							</p>
						) : (
							services.map((service) => {
								const suggestion = serviceToCatalogSuggestion(service);
								const isSelected = selectedUrls.has(suggestion.url);
								const isDisabled =
									!isSelected && selected.length >= MAX_CATALOG_SUGGESTIONS;
								return (
									<button
										key={service._id}
										type="button"
										onClick={() => toggleService(service)}
										disabled={isDisabled}
										className={cn(
											"flex w-full items-center gap-3 rounded-lg bg-card p-3 text-left shadow-custom transition-transform hover:-translate-y-0.5 disabled:opacity-50",
											isSelected && "bg-secondary/10 shadow-md",
										)}
									>
										<div className="relative size-12 shrink-0 overflow-hidden rounded-lg bg-muted">
											{suggestion.imageUrl ? (
												<img
													src={suggestion.imageUrl}
													alt=""
													className="size-full object-cover"
												/>
											) : null}
										</div>
										<div className="min-w-0 flex-1">
											<p className="line-clamp-1 text-sm font-semibold text-foreground">
												{formatServiceDisplayTitle(suggestion.title)}
											</p>
											<p className="line-clamp-2 text-xs text-muted-foreground">
												{suggestion.description}
											</p>
										</div>
										<span className="text-xs font-semibold text-secondary">
											{isSelected ? "Added" : "Add"}
										</span>
									</button>
								);
							})
						)}
					</div>
					<div className="flex justify-end gap-2 border-t border-secondary/20 pt-3">
						<button
							type="button"
							onClick={onClose}
							className="rounded-full bg-secondary px-4 py-2 text-sm font-semibold text-secondary-foreground"
						>
							Done
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
